import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Camera,
  Upload,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  Save,
  Image,
  Ruler,
  Anchor,
  ClipboardCheck,
  FileDown,
  User,
  Mail,
  X,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Pen,
  RotateCcw,
  Copy,
  FileText,
  Calendar
} from "lucide-react";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature Pad Component
const SignaturePad = ({ title, signature, onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Initialize canvas
  useEffect(() => {
    if (isModalOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setIsEmpty(true);
    }
  }, [isModalOpen]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setIsEmpty(false);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const saveSignature = () => {
    if (isEmpty) {
      toast.error("Te rog să semnezi înainte de a salva");
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
    setIsModalOpen(false);
    toast.success("Semnătura a fost salvată!");
  };

  return (
    <div className="space-y-2">
      <Label className="text-[#94A3B8]">{title}</Label>
      
      {signature ? (
        <div className="relative group">
          <div className="bg-white rounded-lg p-2 border border-[#23F7DD]/30 inline-block">
            <img 
              src={signature} 
              alt={title}
              className="h-16 object-contain"
            />
          </div>
          <button
            type="button"
            onClick={onClear}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Trash2 className="w-3 h-3 text-white" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsModalOpen(true)}
          className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
        >
          <Pen className="w-4 h-4 mr-2" />
          Adaugă Semnătură
        </Button>
      )}

      {/* Signature Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
              <Pen className="w-5 h-5 text-[#23F7DD]" />
              {title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">
              Desenează semnătura în căsuța de mai jos folosind mouse-ul sau touchscreen-ul.
            </p>
            
            {/* Canvas */}
            <div className="border-2 border-dashed border-white/20 rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={450}
                height={150}
                className="w-full touch-none cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={clearCanvas}
                className="border-white/20 text-[#94A3B8] hover:text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Șterge
              </Button>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Anulează
                </Button>
                <Button
                  type="button"
                  onClick={saveSignature}
                  className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salvează
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Image Preview Modal Component
const ImagePreviewModal = ({ images, currentIndex, isOpen, onClose, onNavigate }) => {
  if (!isOpen || !images || images.length === 0) return null;
  
  const currentImage = images[currentIndex];
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < images.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-black/95 border-white/10 p-0">
        <div className="relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Navigation arrows */}
          {canGoPrev && (
            <button
              onClick={() => onNavigate(currentIndex - 1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/50 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {canGoNext && (
            <button
              onClick={() => onNavigate(currentIndex + 1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-black/50 hover:bg-black/80 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          
          {/* Image */}
          <div className="flex items-center justify-center min-h-[400px] max-h-[80vh] p-4">
            <img
              src={currentImage}
              alt={`Preview ${currentIndex + 1}`}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
          </div>
          
          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full text-white text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      </DialogContent>>
    </Dialog>
  );
};

// Scaffold Form Component
const ScaffoldForm = ({ scaffold, index, onChange, onRemove, onPreviewPhoto, onMoveUp, onMoveDown, isFirst, isLast, totalScaffolds }) => {
  const fileInputRef = useRef(null);
  const signedCardRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCard, setUploadingCard] = useState(false);

  const handlePhotoUpload = async (e, type = "photos") => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (type === "signed_card") {
      setUploadingCard(true);
      // Signed card - only one file
      try {
        const sigRes = await axios.get(`${API}/cloudinary/signature?folder=projects&resource_type=image`);
        const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
        
        const formData = new FormData();
        formData.append("file", files[0]);
        formData.append("api_key", api_key);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        formData.append("folder", folder);
        
        const cloudinaryRes = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          { method: "POST", body: formData }
        );
        
        const cloudinaryData = await cloudinaryRes.json();
        
        if (cloudinaryData.secure_url) {
          onChange(index, "signed_card_photo", cloudinaryData.secure_url);
          toast.success("Signed card photo uploaded!");
        } else {
          toast.error("Failed to upload photo");
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload photo");
      } finally {
        setUploadingCard(false);
      }
    } else {
      // Regular photos - support multiple files
      setUploading(true);
      const uploadedUrls = [];
      
      try {
        // Get signature once for all uploads
        const sigRes = await axios.get(`${API}/cloudinary/signature?folder=projects&resource_type=image`);
        const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
        
        // Upload each file sequentially
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          
          // Check file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            toast.error(`${file.name} is too large (max 10MB)`);
            continue;
          }
          
          try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("api_key", api_key);
            formData.append("timestamp", timestamp);
            formData.append("signature", signature);
            formData.append("folder", folder);
            
            const cloudinaryRes = await fetch(
              `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
              { method: "POST", body: formData }
            );
            
            const cloudinaryData = await cloudinaryRes.json();
            
            if (cloudinaryData.secure_url) {
              uploadedUrls.push(cloudinaryData.secure_url);
              toast.success(`Photo ${i + 1}/${files.length} uploaded`);
            }
          } catch (err) {
            console.error(`Error uploading ${file.name}:`, err);
          }
        }
        
        if (uploadedUrls.length > 0) {
          const newPhotos = [...(scaffold.photos || []), ...uploadedUrls];
          onChange(index, "photos", newPhotos);
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload photos");
      } finally {
        setUploading(false);
      }
    }
    
    // Reset input
    e.target.value = '';
  };

  const removePhoto = (photoIndex) => {
    const newPhotos = scaffold.photos.filter((_, i) => i !== photoIndex);
    onChange(index, "photos", newPhotos);
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-white flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-[#23F7DD]" />
            Scaffold #{index + 1}
            <span className="text-xs text-[#64748B]">of {totalScaffolds}</span>
          </h4>
          <div className="flex items-center gap-1">
            {/* Move Up Button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveUp(index)}
              disabled={isFirst}
              className={`${isFirst ? 'text-gray-600 cursor-not-allowed' : 'text-[#94A3B8] hover:text-white hover:bg-white/10'}`}
              title="Move up"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            {/* Move Down Button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveDown(index)}
              disabled={isLast}
              className={`${isLast ? 'text-gray-600 cursor-not-allowed' : 'text-[#94A3B8] hover:text-white hover:bg-white/10'}`}
              title="Move down"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
            {/* Delete Button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 ml-2"
              title="Delete scaffold"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Scaffold Name *</Label>
            <Input
              value={scaffold.name || ""}
              onChange={(e) => onChange(index, "name", e.target.value)}
              placeholder="e.g., Main Building East"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Height (m)</Label>
            <Input
              type="number"
              step="0.1"
              value={scaffold.height || ""}
              onChange={(e) => onChange(index, "height", parseFloat(e.target.value) || null)}
              placeholder="e.g., 12.5"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Length (m)</Label>
            <Input
              type="number"
              step="0.1"
              value={scaffold.length || ""}
              onChange={(e) => onChange(index, "length", parseFloat(e.target.value) || null)}
              placeholder="e.g., 25"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Width (m)</Label>
            <Input
              type="number"
              step="0.1"
              value={scaffold.width || ""}
              onChange={(e) => onChange(index, "width", parseFloat(e.target.value) || null)}
              placeholder="e.g., 1.2"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Capacity (kg/m²)</Label>
            <Input
              type="number"
              step="0.1"
              value={scaffold.capacity || ""}
              onChange={(e) => onChange(index, "capacity", parseFloat(e.target.value) || null)}
              placeholder="e.g., 200"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Number of Anchors</Label>
            <Input
              type="number"
              value={scaffold.number_of_anchors || ""}
              onChange={(e) => onChange(index, "number_of_anchors", parseInt(e.target.value) || null)}
              placeholder="e.g., 8"
              className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Status</Label>
          <div className="flex gap-2 flex-wrap">
            {["ok", "needs_repair", "unsafe"].map((statusOption) => (
              <Button
                key={statusOption}
                type="button"
                variant={scaffold.status === statusOption ? "default" : "outline"}
                size="sm"
                onClick={() => onChange(index, "status", statusOption)}
                className={
                  scaffold.status === statusOption
                    ? statusOption === "ok" ? "bg-green-500 text-white hover:bg-green-600" :
                      statusOption === "needs_repair" ? "bg-yellow-500 text-white hover:bg-yellow-600" :
                      "bg-red-500 text-white hover:bg-red-600"
                    : "border-white/20 text-[#94A3B8] hover:text-white"
                }
              >
                {statusOption === "ok" && <CheckCircle2 className="w-4 h-4 mr-1" />}
                {statusOption === "needs_repair" && <AlertTriangle className="w-4 h-4 mr-1" />}
                {statusOption === "unsafe" && <XCircle className="w-4 h-4 mr-1" />}
                {statusOption === "ok" ? "OK" : statusOption === "needs_repair" ? "Needs Repair" : "Unsafe"}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Notes</Label>
          <Textarea
            value={scaffold.notes || ""}
            onChange={(e) => onChange(index, "notes", e.target.value)}
            placeholder="Any additional notes about this scaffold..."
            className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
          />
        </div>

        {/* Photos Section */}
        <div className="space-y-3">
          <Label className="text-[#94A3B8]">Scaffold Photos ({scaffold.photos?.length || 0})</Label>
          <div className="flex flex-wrap gap-3">
            {scaffold.photos && scaffold.photos.map((photo, photoIdx) => (
              <div key={photoIdx} className="relative group">
                <img
                  src={photo}
                  alt={`Scaffold ${index + 1} photo ${photoIdx + 1}`}
                  className="w-24 h-24 object-cover rounded-lg border border-white/10 cursor-pointer hover:border-[#23F7DD]/50 transition-colors"
                  onClick={() => onPreviewPhoto && onPreviewPhoto(scaffold.photos, photoIdx)}
                />
                {/* Zoom icon overlay */}
                <div 
                  className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={() => onPreviewPhoto && onPreviewPhoto(scaffold.photos, photoIdx)}
                >
                  <ZoomIn className="w-6 h-6 text-white" />
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removePhoto(photoIdx); }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-24 h-24 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center text-[#64748B] hover:border-[#23F7DD]/50 hover:text-[#23F7DD] transition-colors"
            >
              {uploading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Camera className="w-6 h-6 mb-1" />
                  <span className="text-xs">Add Photos</span>
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handlePhotoUpload(e, "photos")}
              className="hidden"
            />
          </div>
        </div>

        {/* Signed Card Photo */}
        <div className="space-y-3">
          <Label className="text-[#94A3B8]">Signed Scaffold Card Photo</Label>
          <div className="flex items-center gap-4">
            {scaffold.signed_card_photo ? (
              <div className="relative group">
                <img
                  src={scaffold.signed_card_photo}
                  alt="Signed card"
                  className="w-32 h-24 object-cover rounded-lg border border-[#23F7DD]/30 cursor-pointer hover:border-[#23F7DD] transition-colors"
                  onClick={() => onPreviewPhoto && onPreviewPhoto([scaffold.signed_card_photo], 0)}
                />
                {/* Zoom icon overlay */}
                <div 
                  className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={() => onPreviewPhoto && onPreviewPhoto([scaffold.signed_card_photo], 0)}
                >
                  <ZoomIn className="w-6 h-6 text-white" />
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onChange(index, "signed_card_photo", null); }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => signedCardRef.current?.click()}
                disabled={uploadingCard}
                className="w-32 h-24 border-2 border-dashed border-[#23F7DD]/30 rounded-lg flex flex-col items-center justify-center text-[#64748B] hover:border-[#23F7DD] hover:text-[#23F7DD] transition-colors"
              >
                {uploadingCard ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <Image className="w-6 h-6 mb-1" />
                    <span className="text-xs">Upload Card</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={signedCardRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handlePhotoUpload(e, "signed_card")}
              className="hidden"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function LocalControlReport() {
  const { projectId, reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [project, setProject] = useState(null);
  const [savedReportId, setSavedReportId] = useState(null);
  
  // Image preview state
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    scaffolds: [{ 
      name: "", 
      height: null, 
      length: null, 
      width: null, 
      capacity: null,
      number_of_anchors: null,
      photos: [],
      signed_card_photo: null,
      notes: "",
      status: "ok"
    }],
    inspection_date: new Date().toISOString().split("T")[0],
    inspector_name: "",
    inspector_code: "",
    weather_conditions: "",
    general_notes: "",
    overall_status: "approved",
    // Client info
    client_name: "",
    client_email: "",
    client_phone: "",
    client_company: "",
    // Signatures
    inspector_signature: null,
    client_signature: null
  });

  const isEditing = reportId && reportId !== "new";
  
  // State for available reports to import from
  const [availableReports, setAvailableReports] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Handle photo preview
  const handlePreviewPhoto = (images, index) => {
    setPreviewImages(images);
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  useEffect(() => {
    fetchProject();
    if (isEditing) {
      fetchReport();
    } else {
      // If creating new report, fetch available reports for import
      fetchAvailableReports();
    }
  }, [projectId, reportId]);

  useEffect(() => {
    if (user && !formData.inspector_name) {
      setFormData(prev => ({
        ...prev,
        inspector_name: user.name || "",
        inspector_code: user.worker_code || user.teamleader_code || ""
      }));
    }
  }, [user]);

  const fetchAvailableReports = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}/control-reports`);
      setAvailableReports(res.data || []);
    } catch (error) {
      console.error("Failed to fetch available reports:", error);
    }
  };

  const importFromReport = async (sourceReportId) => {
    setImporting(true);
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}/control-reports/${sourceReportId}`);
      const sourceReport = res.data;
      
      // Copy scaffolds WITH photos but WITHOUT signed_card_photo (card must be new for each inspection)
      const importedScaffolds = (sourceReport.scaffolds || []).map(scaffold => ({
        ...scaffold,
        scaffold_id: null, // Will generate new ID on save
        photos: scaffold.photos || [], // KEEP photos from source
        signed_card_photo: null // Clear signed card - must be new
      }));
      
      setFormData(prev => ({
        ...prev,
        scaffolds: importedScaffolds.length > 0 ? importedScaffolds : prev.scaffolds,
        client_name: sourceReport.client_name || "",
        client_email: sourceReport.client_email || "",
        client_phone: sourceReport.client_phone || "",
        client_company: sourceReport.client_company || "",
        // Reset signatures for new report
        inspector_signature: null,
        client_signature: null
      }));
      
      setShowImportModal(false);
      toast.success(`Importate ${importedScaffolds.length} schele din raportul ${sourceReport.report_code} (cu poze)`);
    } catch (error) {
      toast.error("Eroare la importarea raportului");
    } finally {
      setImporting(false);
    }
  };

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}`);
      setProject(res.data);
    } catch (error) {
      toast.error("Failed to load project");
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}/control-reports/${reportId}`);
      // Ensure each scaffold has photos array and signed_card_photo field
      const scaffoldsWithPhotos = (res.data.scaffolds || []).map(scaffold => ({
        ...scaffold,
        photos: scaffold.photos || [],
        signed_card_photo: scaffold.signed_card_photo || null
      }));
      setFormData({
        scaffolds: scaffoldsWithPhotos,
        inspection_date: res.data.inspection_date || "",
        inspector_name: res.data.inspector_name || "",
        inspector_code: res.data.inspector_code || "",
        weather_conditions: res.data.weather_conditions || "",
        general_notes: res.data.general_notes || "",
        overall_status: res.data.overall_status || "approved",
        client_name: res.data.client_name || "",
        client_email: res.data.client_email || "",
        client_phone: res.data.client_phone || "",
        client_company: res.data.client_company || "",
        inspector_signature: res.data.inspector_signature || null,
        client_signature: res.data.client_signature || null
      });
      setSavedReportId(reportId);
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleScaffoldChange = (index, field, value) => {
    const newScaffolds = [...formData.scaffolds];
    newScaffolds[index] = { ...newScaffolds[index], [field]: value };
    setFormData({ ...formData, scaffolds: newScaffolds });
  };

  const addScaffold = () => {
    setFormData({
      ...formData,
      scaffolds: [
        ...formData.scaffolds,
        { 
          name: "", 
          height: null, 
          length: null, 
          width: null, 
          capacity: null,
          number_of_anchors: null,
          photos: [],
          signed_card_photo: null,
          notes: "",
          status: "ok"
        }
      ]
    });
  };

  const removeScaffold = (index) => {
    if (formData.scaffolds.length === 1) {
      toast.error("You must have at least one scaffold");
      return;
    }
    const newScaffolds = formData.scaffolds.filter((_, i) => i !== index);
    setFormData({ ...formData, scaffolds: newScaffolds });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (formData.scaffolds.some(s => !s.name)) {
      toast.error("Please fill in scaffold names");
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (isEditing) {
        response = await axios.put(`${API}/local-projects/${projectId}/control-reports/${reportId}`, formData);
        toast.success("Report updated!");
        setSavedReportId(reportId);
      } else {
        response = await axios.post(`${API}/local-projects/${projectId}/control-reports`, formData);
        toast.success("Control report created!");
        setSavedReportId(response.data.report_id);
      }
      
      // Show PDF download option
      toast.info("Click 'Download PDF' to save the report", { duration: 5000 });
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save report");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    const currentReportId = savedReportId || reportId;
    if (!currentReportId) {
      toast.error("Please save the report first");
      return;
    }

    setGeneratingPdf(true);
    try {
      // Open HTML report in new tab for printing
      const token = localStorage.getItem('token');
      const pdfUrl = `${API}/local-projects/${projectId}/control-reports/${currentReportId}/pdf`;
      
      // Fetch the HTML content
      const response = await axios.get(pdfUrl, { responseType: 'text' });
      
      // Open in new window with print styles
      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.data);
      printWindow.document.close();
      
      // Add print button and auto-print prompt
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success("Report opened! Use Ctrl+P or Cmd+P to save as PDF");
    } catch (error) {
      console.error("PDF Error:", error);
      toast.error("Failed to generate report. Please try again.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-10 h-10 text-[#23F7DD] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title={isEditing ? "Edit Control Report" : "New Control Report"}>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="control-report-form">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/local-projects/${projectId}`)}
              className="text-[#94A3B8] hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Unbounded']">
                {isEditing ? "Edit Control Report" : "New Control Report"}
              </h1>
              {project && (
                <p className="text-[#94A3B8]">{project.title}</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-3">
            {/* Import from previous report button - only show when creating new */}
            {!isEditing && availableReports.length > 0 && (
              <Button
                type="button"
                onClick={() => setShowImportModal(true)}
                variant="outline"
                className="border-[#FF5F1F]/50 text-[#FF5F1F] hover:bg-[#FF5F1F]/10"
              >
                <Copy className="w-4 h-4 mr-2" />
                Import din raport anterior
              </Button>
            )}
            {savedReportId && (
              <Button
                type="button"
                onClick={downloadPdf}
                disabled={generatingPdf}
                variant="outline"
                className="border-[#23F7DD]/50 text-[#23F7DD] hover:bg-[#23F7DD]/10"
              >
                {generatingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditing ? "Update Report" : "Save Report"}
            </Button>
          </div>
        </div>

        {/* Client Information */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <User className="w-5 h-5 text-[#23F7DD]" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Client Name</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="e.g., John Smith"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Client Email</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  placeholder="e.g., client@company.com"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Client Phone</Label>
                <Input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  placeholder="e.g., +47 123 45 678"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Company Name</Label>
                <Input
                  value={formData.client_company}
                  onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                  placeholder="e.g., ABC Construction AS"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inspection Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">Inspection Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Inspection Date</Label>
                <Input
                  type="date"
                  value={formData.inspection_date}
                  onChange={(e) => setFormData({ ...formData, inspection_date: e.target.value })}
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Inspector Name</Label>
                <Input
                  value={formData.inspector_name}
                  onChange={(e) => setFormData({ ...formData, inspector_name: e.target.value })}
                  placeholder="Your name"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Inspector Code</Label>
                <Input
                  value={formData.inspector_code}
                  onChange={(e) => setFormData({ ...formData, inspector_code: e.target.value })}
                  placeholder="e.g., TL-001"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Weather Conditions</Label>
                <Input
                  value={formData.weather_conditions}
                  onChange={(e) => setFormData({ ...formData, weather_conditions: e.target.value })}
                  placeholder="e.g., Sunny, 15°C"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Overall Status</Label>
              <div className="flex gap-3 flex-wrap">
                {[
                  { value: "approved", label: "Approved", icon: CheckCircle2, color: "#22c55e" },
                  { value: "conditional", label: "Conditional", icon: AlertTriangle, color: "#eab308" },
                  { value: "rejected", label: "Rejected", icon: XCircle, color: "#ef4444" }
                ].map(({ value, label, icon: Icon, color }) => (
                  <Button
                    key={value}
                    type="button"
                    variant={formData.overall_status === value ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, overall_status: value })}
                    className={
                      formData.overall_status === value
                        ? "text-white"
                        : "border-white/20 text-[#94A3B8] hover:text-white"
                    }
                    style={formData.overall_status === value ? { backgroundColor: color } : {}}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scaffolds */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Scaffolds ({formData.scaffolds.length})</h3>
            <Button
              type="button"
              onClick={addScaffold}
              variant="outline"
              className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Scaffold
            </Button>
          </div>

          <div className="space-y-4">
            {formData.scaffolds.map((scaffold, index) => (
              <ScaffoldForm
                key={scaffold.scaffold_id || index}
                scaffold={scaffold}
                index={index}
                onChange={handleScaffoldChange}
                onRemove={removeScaffold}
                onPreviewPhoto={handlePreviewPhoto}
                onMoveUp={(idx) => {
                  if (idx > 0) {
                    const newScaffolds = [...formData.scaffolds];
                    [newScaffolds[idx - 1], newScaffolds[idx]] = [newScaffolds[idx], newScaffolds[idx - 1]];
                    setFormData({ ...formData, scaffolds: newScaffolds });
                    toast.success(`Scaffold moved up to position ${idx}`);
                  }
                }}
                onMoveDown={(idx) => {
                  if (idx < formData.scaffolds.length - 1) {
                    const newScaffolds = [...formData.scaffolds];
                    [newScaffolds[idx], newScaffolds[idx + 1]] = [newScaffolds[idx + 1], newScaffolds[idx]];
                    setFormData({ ...formData, scaffolds: newScaffolds });
                    toast.success(`Scaffold moved down to position ${idx + 2}`);
                  }
                }}
                isFirst={index === 0}
                isLast={index === formData.scaffolds.length - 1}
                totalScaffolds={formData.scaffolds.length}
              />
            ))}
          </div>
        </div>

        {/* General Notes */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">General Notes</Label>
              <Textarea
                value={formData.general_notes}
                onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })}
                placeholder="Any additional notes about this inspection..."
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD] min-h-[100px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Signatures Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <Pen className="w-5 h-5 text-[#23F7DD]" />
              Semnături
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Inspector Signature */}
              <SignaturePad
                title="Semnătură Inspector"
                signature={formData.inspector_signature}
                onSave={(sig) => setFormData({ ...formData, inspector_signature: sig })}
                onClear={() => setFormData({ ...formData, inspector_signature: null })}
              />
              
              {/* Client Signature */}
              <SignaturePad
                title="Semnătură Client"
                signature={formData.client_signature}
                onSave={(sig) => setFormData({ ...formData, client_signature: sig })}
                onClear={() => setFormData({ ...formData, client_signature: null })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/local-projects/${projectId}`)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          {savedReportId && (
            <Button
              type="button"
              onClick={downloadPdf}
              disabled={generatingPdf}
              variant="outline"
              className="border-[#23F7DD]/50 text-[#23F7DD] hover:bg-[#23F7DD]/10"
            >
              {generatingPdf ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <FileDown className="w-4 h-4 mr-2" />
              )}
              Download PDF
            </Button>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold min-w-[150px]"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditing ? "Update Report" : "Save Report"}
          </Button>
        </div>
      </form>
      
      {/* Image Preview Modal */}
      <ImagePreviewModal
        images={previewImages}
        currentIndex={previewIndex}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onNavigate={setPreviewIndex}
      />
      
      {/* Import from Previous Report Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#FF5F1F]" />
              Import din Raport Anterior
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">
              Selectează un raport din care să importezi schelele. Fotografiile și semnăturile nu vor fi copiate.
            </p>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {availableReports.map((report) => (
                <div
                  key={report.report_id}
                  className="p-4 rounded-lg border border-white/10 hover:border-[#FF5F1F]/50 cursor-pointer transition-colors bg-black/30 hover:bg-black/50"
                  onClick={() => !importing && importFromReport(report.report_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#FF5F1F]" />
                      <div>
                        <p className="font-medium text-white">{report.report_code}</p>
                        <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                          <Calendar className="w-3 h-3" />
                          {report.inspection_date}
                          <span>•</span>
                          <span>{report.scaffolds?.length || 0} schele</span>
                        </div>
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={
                        report.overall_status === 'approved' 
                          ? 'border-green-500/50 text-green-400' 
                          : report.overall_status === 'conditional'
                          ? 'border-yellow-500/50 text-yellow-400'
                          : 'border-red-500/50 text-red-400'
                      }
                    >
                      {report.overall_status === 'approved' ? 'Aprobat' : 
                       report.overall_status === 'conditional' ? 'Condiționat' : 'Respins'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowImportModal(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Anulează
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
